/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { LocalDate } from 'js-joda';
import { SelectField } from 'ui';
import { Typography } from 'omakase';
import { core } from '@tock/coretypes';
import { getTitleForType } from 'utils/importantDateUtils';
import { useIntl } from 'react-intl';
import ImportantDateIcon from 'components/profile/ImportantDateIcon';
import _ from 'lodash';

export type DateUpdate = { month: number; day: number } | 'delete';

interface Props {
  type: core.PatronProfile.DayType;
  month?: number;
  day?: number;
  onChangeDate(type: core.PatronProfile.DayType, update: DateUpdate): void;
}

const ImportantDateEditorRow: React.FunctionComponent<Props> = ({
  type,
  month: propMonth,
  day: propDay,
  onChangeDate,
}) => {
  const intl = useIntl();
  const [mostRecentPropMonth, setMostRecentPropMonth] = React.useState<number>();
  const [mostRecentPropDay, setMostRecentPropDay] = React.useState<number>();

  const [selectedMonth, setSelectedMonth] = React.useState(propMonth);
  const [selectedDay, setSelectedDay] = React.useState(propDay);

  React.useEffect(() => {
    if (propMonth !== mostRecentPropMonth) {
      setMostRecentPropMonth(propMonth);
      setSelectedMonth(propMonth);
    }
    if (propDay !== mostRecentPropDay) {
      setMostRecentPropDay(propDay);
      setSelectedDay(propDay);
    }
  }, [propMonth, mostRecentPropMonth, propDay, mostRecentPropDay]);

  /**
   * Handler for changing the value of the month select dropdown. In the case of
   * months, we clear the day selection if the month selection is undefined,
   * since selecting an empty month is like clearing the selection.
   */
  const handleChangeMonth = (e: React.ChangeEvent<any>) => {
    const newValue = e.target.value;
    if (!newValue) {
      setSelectedMonth(undefined);
      setSelectedDay(undefined);
      handleUpdateDate(undefined, undefined);
    } else {
      const month = _.toNumber(e.target.value);
      setSelectedMonth(month);
      handleUpdateDate(month, selectedDay);
    }
  };

  /**
   * Handler for changing the value of the day select dropdown. In contrast to
   * months, this handler is simpler because months don't respond to day change
   * events, only the other way around.
   */
  const handleChangeDay = (e: React.ChangeEvent<any>) => {
    const day = _.toNumber(e.target.value);
    setSelectedDay(day);
    handleUpdateDate(selectedMonth, day);
  };

  /**
   * Takes values of the month and date dropdown and determines what date change
   * the given selections correspond to, and then calls onChangeDate with that
   * change.
   */
  const handleUpdateDate = (m: number | undefined, d: number | undefined) => {
    if (m && d) {
      onChangeDate(type, { month: m, day: d });
    } else {
      onChangeDate(type, 'delete');
    }
  };

  // Month options are always Jan - Dec, localized to use the user locale's
  // words for those months.
  const monthOptions = _.range(0, 12).map((monthNumber) => {
    const date = new Date(Date.UTC(2000, monthNumber, 10, 0, 0, 0));
    const monthString = intl.formatDate(date, { month: 'long' });
    const val = monthNumber + 1;
    return (
      <option key={`${type}-month-${val}`} value={_.toString(val)}>
        {monthString}
      </option>
    );
  });

  // For day options, only populate the list with days that can actually be in
  // the given month.
  let dayOptions: JSX.Element[] = [];
  if (selectedMonth) {
    // Pick the year 2000 basically arbitrarily. The only special quality it has
    // is that it's a leap year, which we need to get Feb 29.
    const lastDayOfSelectedMonth = LocalDate.of(2000, selectedMonth, 1).plusMonths(1).minusDays(1);
    const dayOfMonth = lastDayOfSelectedMonth.dayOfMonth();
    dayOptions = _.range(1, dayOfMonth + 1).map((day) => (
      <option key={`${type}-day-${day}`} value={_.toString(day)}>
        {day}
      </option>
    ));
    if (selectedDay && selectedDay > dayOfMonth) {
      setSelectedDay(undefined);
    }
  }

  return (
    <div className="ImportantDateEdit-dayEditItem">
      <div className="ImportantDateEdit-dayType mobile">
        <Typography color="inherit" component="span" variant="body02">
          {getTitleForType(type)}
        </Typography>
      </div>
      <div className="ImportantDateEdit-dayEditItem-wrapper">
        <div className="ImportantDateEdit-dayTypeIndicator">
          <ImportantDateIcon type={type} className="EditModal" />
        </div>
        <div className="ImportantDateEdit-dayInformation">
          <div className="ImportantDateEdit-dayType">
            <Typography color="inherit" component="span" variant="body02">
              {getTitleForType(type)}
            </Typography>
          </div>
          <ImportantDateFields
            type={type}
            selectedMonth={selectedMonth}
            handleChangeMonth={handleChangeMonth}
            monthOptions={monthOptions}
            selectedDay={selectedDay}
            handleChangeDay={handleChangeDay}
            dayOptions={dayOptions}
          />
        </div>
      </div>
    </div>
  );
};

interface ImportantDateFieldsProps {
  type: core.PatronProfile.DayType;
  selectedMonth: number | undefined;
  handleChangeMonth: (e: React.ChangeEvent<any>) => void;
  monthOptions: JSX.Element[];
  selectedDay: number | undefined;
  handleChangeDay: (e: React.ChangeEvent<any>) => void;
  dayOptions: JSX.Element[];
}

const ImportantDateFields = ({
  type,
  selectedMonth,
  handleChangeMonth,
  monthOptions,
  selectedDay,
  handleChangeDay,
  dayOptions,
}: ImportantDateFieldsProps) => {
  return (
    <div className="ImportantDateEdit-daySelector">
      <SelectField
        label="Month"
        name={`${type}-month`}
        id={`${type}-month`}
        className="ImportantDateEdit-daySelector-field"
        value={_.toString(selectedMonth)}
        onChange={handleChangeMonth}
      >
        <option value="" />
        {monthOptions}
      </SelectField>
      <SelectField
        label="Day"
        name={`${type}-day`}
        id={`${type}-day`}
        className="ImportantDateEdit-daySelector-field DayField"
        disabled={!selectedMonth}
        value={_.toString(selectedDay)}
        onChange={handleChangeDay}
      >
        <option value="" />
        {dayOptions}
      </SelectField>
    </div>
  );
};

export default ImportantDateEditorRow;
